import { useMemo } from 'react'

import { Conversation as CoreConversation, groupConversations } from '../core'
import useMaxChatStore from '../stores/use-max-chat-store'
import { MaxConversationGroup, MaxConversationSummary } from '../types'

const toCoreConversation = (conversation: MaxConversationSummary): CoreConversation => ({
  id: conversation.id,
  title: conversation.title || 'Untitled Conversation',
  preview: conversation.preview || '',
  status: 'active',
  updatedAt: new Date(conversation.updatedAt),
})

const toSummaryFromCoreConversation = (
  conversation: CoreConversation,
  options?: { pinned?: boolean }
): MaxConversationSummary => ({
  id: conversation.id,
  title: conversation.title || 'Untitled Conversation',
  preview: conversation.preview || '',
  updatedAt: new Date(conversation.updatedAt).getTime(),
  isPinned: options?.pinned ?? false,
})

const useMaxConversations = () => {
  const conversations = useMaxChatStore((state) => state.conversations)
  const pinnedConversations = useMaxChatStore((state) => state.pinnedConversations)
  const searchQuery = useMaxChatStore((state) => state.searchQuery)
  const activeConversationId = useMaxChatStore((state) => state.activeConversationId)

  const setConversationCollections = useMaxChatStore((state) => state.setConversationCollections)
  const upsertConversation = useMaxChatStore((state) => state.upsertConversation)
  const removeConversation = useMaxChatStore((state) => state.removeConversation)
  const renameConversationLocal = useMaxChatStore((state) => state.renameConversationLocal)
  const pinConversationLocal = useMaxChatStore((state) => state.pinConversationLocal)
  const unpinConversationLocal = useMaxChatStore((state) => state.unpinConversationLocal)
  const setSearchQuery = useMaxChatStore((state) => state.setSearchQuery)
  const clearSearch = useMaxChatStore((state) => state.clearSearch)
  const setActiveConversationId = useMaxChatStore((state) => state.setActiveConversationId)
  const resetConversationState = useMaxChatStore((state) => state.resetConversationState)

  const groupedConversations = useMemo<MaxConversationGroup[]>(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const matchesQuery = (conversation: MaxConversationSummary): boolean => {
      if (!normalizedQuery) return true
      return (
        conversation.title.toLowerCase().includes(normalizedQuery) ||
        conversation.preview.toLowerCase().includes(normalizedQuery)
      )
    }

    const visiblePinned = pinnedConversations.filter(matchesQuery)
    const visibleRegular = conversations.filter(matchesQuery)

    const regularGroups = groupConversations(visibleRegular.map(toCoreConversation)).map((group) => ({
      label: group.label,
      conversations: group.conversations.map((conversation) =>
        toSummaryFromCoreConversation(conversation, { pinned: false })
      ),
    }))

    if (visiblePinned.length === 0) {
      return regularGroups
    }

    return [{ label: 'Pinned', conversations: visiblePinned }, ...regularGroups]
  }, [conversations, pinnedConversations, searchQuery])

  const hasConversations = conversations.length > 0 || pinnedConversations.length > 0
  const conversationCount = conversations.length + pinnedConversations.length

  return {
    conversations,
    pinnedConversations,
    groupedConversations,
    searchQuery,
    activeConversationId,
    hasConversations,
    conversationCount,

    setConversationCollections,
    upsertConversation,
    removeConversation,
    renameConversationLocal,
    pinConversationLocal,
    unpinConversationLocal,
    setSearchQuery,
    clearSearch,
    setActiveConversationId,
    resetConversationState,
  }
}

export default useMaxConversations
